import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  selectProductListState,
  setFilterQueryState,
  setIsFromVisualNav,
  setServerData,
} from "@/store/features/productListSlice"
import { DOUBLE_QUOTE_REGEX, BACKSLASH_REGEX } from "@/constants/regex"
import { VisualFilter } from "@kohler-dsm-library/kohler-modules"
import { dataLayerForImageClick } from "@/components/Shared/VisualNav/analytics/index"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"
import { pushAnalyticsObjToDataLayer } from "@/utils/helper"
import htmlParser from "html-react-parser"

const VisualNavigation = (data = {}) => {
  const dispatch = useDispatch()
  const productlistState = useSelector(selectProductListState)
  const {
    facet_labels: facetLabels = {},
    currentAppliedfiltersVisualNav = [],
    isServerData = false,
  } = productlistState

  const myAuthoredData = data?.data[":items"]
  const productArray = Object.values(myAuthoredData)

  const [activeCard, setActiveCard] = useState()
  const suppressSelectionCheck = useRef(false)

  // Accessibility workaround
  const wrapperRef = useRef(null)

  const formatVisualNavFilterName = query =>
    facetLabels[query]?.replace(" ", "_") ?? query

  const removeVisualNavFilter = (filterName, filterValue) => {
    const updatedFilters = currentAppliedfiltersVisualNav.filter(filterStr => {
      const [facet, value] = filterStr.split(" - ")
      return !(facet === filterName && value === filterValue)
    })

    const updatedQuery = updatedFilters.length
      ? updatedFilters
          .map(f => {
            const [facet, value] = f.split(" - ")
            return `${formatVisualNavFilterName(facet)}:${value
              .replace(DOUBLE_QUOTE_REGEX, '\\"')
              ?.replace(BACKSLASH_REGEX, "")}`
          })
          .join(",")
      : "NONE"

    dispatch(setFilterQueryState(updatedQuery))
    dispatch(setIsFromVisualNav(true))
    dispatch(setServerData(false))
  }

  const handleNavSelection = (
    filterName,
    filterValue,
    linkHref,
    visualNavType,
    newTab,
    index,
    title,
    imgeUrl
  ) => {
    if (visualNavType === "plpFilterNav") {
      suppressSelectionCheck.current = true
      const filteredValue = filterValue
        ?.replace(DOUBLE_QUOTE_REGEX, '\\"')
        ?.replace(BACKSLASH_REGEX, "")
      const cardKey = `${filterName}-${filterValue}`
      const cartlength = productArray?.length || 0
      if (activeCard === cardKey) {
        dataLayerForImageClick({
          isSlected: true,
          index: index,
          productLength: cartlength,
          title: title,
          imageUrl: imgeUrl,
        })
        removeVisualNavFilter(filterName, filterValue)
        dispatch(setFilterQueryState(""))
        setActiveCard(undefined)
      } else {
        dataLayerForImageClick({
          isSlected: false,
          index: index,
          productLength: cartlength,
          title: title,
          imageUrl: imgeUrl,
        })
        dispatch(
          setFilterQueryState(
            `${formatVisualNavFilterName(filterName)}:${filteredValue}`
          )
        )
        dispatch(setIsFromVisualNav(true))
        dispatch(setServerData(false))
        setActiveCard(cardKey)
      }
    } else {
      // Navigate to link
      if (linkHref) {
        newTab
          ? window.open(linkHref, "_blank")
          : (window.location.href = linkHref)
      }
    }
  }

  useEffect(() => {
    if (suppressSelectionCheck.current) {
      suppressSelectionCheck.current = false
      return
    }

    const matchedIndex = productArray.findIndex(({ filterName, filterValue }) =>
      currentAppliedfiltersVisualNav.some(filterStr => {
        const [facet, value] = filterStr.split(" - ")
        return facet === filterName && value === filterValue
      })
    )

    if (matchedIndex !== -1) {
      const matchedItem = productArray[matchedIndex]
      const cardKey = `${matchedItem.filterName}-${matchedItem.filterValue}`
      setActiveCard(cardKey)
      dispatch(
        setFilterQueryState(
          `${formatVisualNavFilterName(matchedItem.filterName)}:${
            matchedItem.filterValue
          }`
        )
      )
    } else {
      setActiveCard(undefined)
    }
  }, [currentAppliedfiltersVisualNav, isServerData])

  // ✅ Build cards array
  const cardsArray = Object.values(myAuthoredData).map(item => {
    const card = {
      image: {
        src: item.imageUrl || "",
        alt: item.imageAltText || "",
      },
      title: {
        text: htmlParser(item.title) || "",
      },
      visualNavType: item.visualNavType || "",
      newTab: item.newTab || false,
      "aria-label": item.imageAltText,
      // role: "group",
      // "aria-roledescription": "slide",
    }

    if (item.visualNavType === "plpFilterNav") {
      card.filtername = item.filterName || ""
      card.filtervalue = item.filterValue || ""
    } else {
      card.href = modifyHref(item.link)
    }

    return card
  })

  const handleLeftrightclick = event => {
    const target = event.target.closest(
      '[data-datalayer-id="kds-element-visual-nav"]'
    )
    if (target) {
      const rawEventInfo = JSON.parse(
        target?.attributes?.["data-datalayer-data"]?.nodeValue || "{}"
      )
      const commonEventInfo = {
        eventMsg: rawEventInfo.eventMsg || "n/a",
        eventStatus: rawEventInfo.eventStatus || "n/a",
        eventType: rawEventInfo.eventType || "navigation",
        internalLinkPosition: rawEventInfo?.internalLinkPosition || "plp",
        internalLinkZoneName:
          rawEventInfo?.internalLinkZoneName || "plp:carousel image slider",
        internalLinkType:
          rawEventInfo?.internalLinkType || "plp:carousel image slider",
        internalLinkURL: rawEventInfo?.internalLinkURL || "n/a",
      }
      let eventInfo = {}
      eventInfo = {
        ...rawEventInfo,
        ...commonEventInfo,
      }
      pushAnalyticsObjToDataLayer({
        ...eventInfo,
      })
    }
  }

  useEffect(() => {
    document.addEventListener("click", handleLeftrightclick)
    return () => document.removeEventListener("click", handleLeftrightclick)
  }, [])

  const props = {
    mod: "white",
    cards: cardsArray,
    customCarouselProps: {
      "aria-roledescription": "carousel",
      "aria-label": "Shop by Type",
      "aria-live": "polite",
      role: "region",
      leftCarouselControlProps: {
        trackingAttributes: {
          "data-datalayer-id": "kds-element-visual-nav",
          "data-datalayer-data": JSON.stringify({
            clickInternalLinks: "true",
            eventAction: "plp:carousel image slider:left",
            eventName: "plp:carousel image slider:left",
            internalLinkName: "left arrow",
          }),
        },
      },
      rightCarouselControlProps: {
        trackingAttributes: {
          "data-datalayer-id": "kds-element-visual-nav",
          "data-datalayer-data": JSON.stringify({
            clickInternalLinks: "true",
            eventAction: "plp:carousel image slider:right",
            eventName: "plp:carousel image slider:right",
            internalLinkName: "right arrow",
          }),
        },
      },
    },
  }
  const { cards, ...rest } = props

  const modifiedCards = cards.map((card, index) => {
    const cardKey = `${card.filtername}-${card.filtervalue}`
    return {
      ...card,
      onClick: () =>
        handleNavSelection(
          card.filtername,
          card.filtervalue,
          card.href,
          card.visualNavType,
          card.newTab,
          index,
          card.title.text,
          card.image.src
        ),
      isActive: card.visualNavType === "plpFilterNav" && activeCard === cardKey,
    }
  })

  useEffect(() => {
    if (!wrapperRef.current) return

    const container = wrapperRef.current.querySelector(
      ".kohler-carousel-container"
    )
    if (!container) return

    container.setAttribute("aria-live", "polite")

    Array.from(container.children).forEach((slide, index) => {
      slide.setAttribute("role", "group")
      slide.setAttribute("aria-roledescription", "slide")
      slide.setAttribute(
        "aria-label",
        `${modifiedCards[index]?.title?.text || ""} - Slide ${index + 1} of ${
          modifiedCards.length
        }`
      )
    })
  }, [modifiedCards])

  return (
    <div className="visualNavWrapper" ref={wrapperRef}>
      <VisualFilter {...rest} cards={modifiedCards} />
    </div>
  )
}

export default VisualNavigation
